import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TwoFactorVerificationResponse, UserSettingsService } from '../../../../../../services/members/user-settings.service';
import { GrowlerService } from '../../../../../shared/growler/growler.service';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { SharedModule } from '../../../../../shared/shared.module';
import { OtpCodeInputComponent } from '../../../../../v2-layout/shared/otp-code-input/otp-code-input.component';
import { FlexSpacerDirective } from '../../../../../v2-layout/shared/directives/flex-spacer.directive';
import { E2eIdDirective } from '../../../../../v2-layout/shared/directives/e2e-id.directive';
import { LinkButtonDirective } from '../../../../../v2-layout/shared/directives/button.directive';
import { NgxMaskDirective } from 'ngx-mask';

export interface ConfirmTwoFactorInput
{
    type: 'google' | 'sms' | null;

    /**
     * Whose 2FA to challenge. Optional: the login/reset gates pass it explicitly (the user is not
     * authenticated yet), while the Settings paths omit it and the UserSettingsService falls back to
     * the authenticated user. Declared required until SD-8082, but every Settings call site omitted
     * it behind a `<ConfirmTwoFactorInput>` cast — which is what hid the mismatch.
     */
    userId?: number;
    canGoBack?: boolean;
    showTrustDevice?: boolean;
}

export interface ConfirmTwoFactorOutput
{
    success: boolean;
    type?: 'google' | 'sms' | 'recovery';
    token?: string;
    trustDevice?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-confirm-2fa-code',
    templateUrl: './confirm-2fa-code.component.html',
    styleUrls: ['./confirm-2fa-code.component.scss'],
    standalone: true,
    imports: [
        E2eIdDirective,
        LinkButtonDirective,
        NgxMaskDirective,
        SharedModule,
        MatButtonModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        MatDividerModule,
        OtpCodeInputComponent,
        FlexSpacerDirective,
    ],
})
export class Confirm2faCodeComponent implements OnInit, OnDestroy
{
    @ViewChild(OtpCodeInputComponent) private _otp: OtpCodeInputComponent;

    isSubmitting = false;
    isSuccess = false;
    type: 'google' | 'sms' | 'recovery' | null = null;
    canGoBack = true;

    /** Free-text entry for a recovery (backup) code — distinct from the 6-digit OTP (SD-8082). */
    recoveryCode = '';

    /** The method the user switched away from when choosing "use a recovery code", so Back returns to it. */
    private _previousType: 'google' | 'sms' | null = null;
    isSendingSms = false;
    isResending = false;
    showTrustDevice = false;
    trustDevice = false;
    hasError = false;

    /** Current entry from the shared OTP control (mirrors its valueChange). */
    code = '';

    /** Seconds left before the sent SMS code is treated as expired (drives the countdown). */
    secondsRemaining = 0;
    isExpired = false;

    /** True when an SMS send/resend failed — surfaces the resend affordance so a failed send
     *  isn't a dead-end (the modal can open directly in SMS mode, so there's no method screen to
     *  fall back to). */
    smsSendFailed = false;

    /**
     * How long a sent SMS code stays valid. The code lives on Twilio Verify (default 10 min),
     * not in PayHOA, and the send endpoint returns no expiry — so this is a client-side
     * approximation of that window. The server remains the source of truth: a truly-expired
     * code still fails validation regardless of this countdown.
     */
    private readonly _codeValiditySeconds = 600;

    private _errorTimer: ReturnType<typeof setTimeout> | null = null;
    private _successTimer: ReturnType<typeof setTimeout> | null = null;
    private _countdownSub: Subscription | null = null;

    constructor(
        private _userSettingsService: UserSettingsService,
        private _growler: GrowlerService,
        private _dialogRef: MatDialogRef<Confirm2faCodeComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ConfirmTwoFactorInput,
    ) {
    }

    ngOnInit(): void {
        this.type = this.data.type;

        if (this.type === 'sms') {
            this.setSmsType();
        }

        if (this.data.hasOwnProperty('canGoBack')) {
            this.canGoBack = this.data.canGoBack;
        }

        this.showTrustDevice = !!this.data.showTrustDevice;
    }

    setSmsType() {
        this.isSendingSms = true;
        this._userSettingsService.sendSmsVerificationCode(this.data.userId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isSendingSms = false;
                    this.type = 'sms';
                    this.startCountdown();
                    setTimeout(() => this._otp?.focusFirst());
                },
                error: () => {
                    this._growler.error('Error', 'We encountered an error while trying to send the code.');
                    this.isSendingSms = false;
                    this.smsSendFailed = true;
                },
            });
    }

    setGoogleType() {
        this.type = 'google';
        setTimeout(() => this._otp?.focusFirst());
    }

    /** Switch from OTP entry to recovery-code entry, remembering the method to return to. */
    setRecoveryType() {
        this._previousType = this.type as 'google' | 'sms';
        this.stopCountdown();
        this.type = 'recovery';
        this.recoveryCode = '';
        this.hasError = false;
    }

    /** Return from recovery-code entry to the method the user came from. */
    backFromRecovery() {
        this.type = this._previousType;
        this.recoveryCode = '';
        this.hasError = false;
    }

    /** Re-send the SMS code to the enrolled number and restart the validity countdown. */
    resendCode() {
        if (this.isResending) {
            return;
        }

        this.isResending = true;
        this._userSettingsService.sendSmsVerificationCode(this.data.userId)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.isResending = false;
                    this.code = '';
                    this._otp?.reset();
                    this.startCountdown();
                    setTimeout(() => this._otp?.focusFirst());
                    this._growler.success('Code sent', 'We\'ve sent a new code to your phone.');
                },
                error: () => {
                    this.isResending = false;
                    this.smsSendFailed = true;
                    this._growler.error('Error', 'We couldn\'t resend the code. Please try again.');
                },
            });
    }

    /** Return to the type-selection screen and clear any partial entry. */
    goBack() {
        this.stopCountdown();
        this.type = null;
        this.code = '';
    }

    get subtitle(): string {
        if (!this.type) {
            return 'Please select how you would like to verify your identity';
        }
        if (this.type === 'recovery') {
            return 'Enter one of your saved recovery codes';
        }
        if (this.type === 'sms') {
            return 'Enter the 6-digit code sent to your device';
        }
        return 'Open your authenticator app to get your code';
    }

    /** Hint line for the recovery-code entry screen (mirrors the OTP hintText). */
    get recoveryHint(): string {
        if (this.hasError) {
            return 'That code was not accepted — please try another';
        }
        if (this.isSuccess) {
            // Deliberately neutral: this screen is also reached from Settings → regenerate
            // recovery codes, where the user is re-authenticating rather than logging in.
            return 'Verified!';
        }
        if (this.isSubmitting) {
            return 'Verifying...';
        }
        return 'Enter a backup code (for example, ABCDE-12345)';
    }

    get hintText(): string {
        if (this.hasError) {
            return 'Incorrect code — please try again';
        }
        if (this.isSuccess) {
            // Neutral for the same reason as recoveryHint: this screen is opened for password
            // reset and from Settings (disable a method, regenerate recovery codes), none of
            // which are a login.
            return 'Verified!';
        }
        if (this.isSubmitting) {
            return 'Verifying...';
        }
        return `${this.code.length} of 6 digits entered`;
    }

    /** Whether to show the countdown / resend row — SMS only, once a send has been attempted
     *  (a running countdown, an expired code, or a failed send all offer resend). */
    get showResendRow(): boolean {
        return this.type === 'sms' && (this.secondsRemaining > 0 || this.isExpired || this.smsSendFailed);
    }

    /** Status line shown alongside the resend button on the SMS screen. */
    get resendStatus(): string {
        if (this.smsSendFailed) {
            return 'We couldn\'t send a code';
        }
        if (this.isExpired) {
            return 'Code expired';
        }
        return `Code expires in ${this.countdownDisplay}`;
    }

    /** Remaining validity as mm:ss (e.g. "9:47"). */
    get countdownDisplay(): string {
        const total = Math.max(0, this.secondsRemaining);
        const minutes = Math.floor(total / 60);
        const seconds = total % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    onCodeChange(code: string): void {
        this.code = code;
    }

    submit(code: string = this.code) {
        if (code.length !== 6) {
            return;
        }

        this.isSubmitting = true;

        const onSuccess = (response: TwoFactorVerificationResponse) => {
            this.isSubmitting = false;
            this.isSuccess = true;
            this._successTimer = setTimeout(() => {
                // Annotated const rather than a cast, so excess-property checking applies.
                const result: ConfirmTwoFactorOutput = {
                    success: true,
                    type: this.type,
                    token: response.verificationKey,
                    trustDevice: this.trustDevice,
                };
                this._dialogRef.close(result);
            }, 800);
        };

        const onError = () => {
            this.isSubmitting = false;
            this.triggerError();
        };

        if (this.type === 'google') {
            this._userSettingsService.verifyGoogleAuthCode(code, this.data.userId, this.trustDevice)
                .pipe(untilDestroyed(this))
                .subscribe({ next: onSuccess, error: onError });
        } else if (this.type === 'sms') {
            this._userSettingsService.verifySmsCode(code, this.data.userId, this.trustDevice)
                .pipe(untilDestroyed(this))
                .subscribe({ next: onSuccess, error: onError });
        }
    }

    /** Redeem a recovery (backup) code. On success it threads back as type 'recovery' so the
     *  login/reset caller replays it to the gate exactly like a google/sms verification key. */
    submitRecovery() {
        const code = this.recoveryCode.trim();
        if (code.length < 10 || this.isSubmitting || this.isSuccess) {
            return;
        }

        this.isSubmitting = true;

        this._userSettingsService.validateRecoveryCode(code, this.data.userId, this.trustDevice)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (response: TwoFactorVerificationResponse) => {
                    this.isSubmitting = false;
                    this.isSuccess = true;
                    this._successTimer = setTimeout(() => {
                        // Annotated const rather than a cast, so excess-property checking applies.
                        const result: ConfirmTwoFactorOutput = {
                            success: true,
                            type: 'recovery',
                            token: response.verificationKey,
                            trustDevice: this.trustDevice,
                        };
                        this._dialogRef.close(result);
                    }, 800);
                },
                error: () => {
                    this.isSubmitting = false;
                    this.triggerRecoveryError();
                },
            });
    }

    ngOnDestroy(): void {
        if (this._errorTimer) { clearTimeout(this._errorTimer); }
        if (this._successTimer) { clearTimeout(this._successTimer); }
        this.stopCountdown();
    }

    private triggerError() {
        this.hasError = true;
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.code = '';
            this._otp?.reset();
        }, 600);
    }

    private triggerRecoveryError() {
        this.hasError = true;
        this._errorTimer = setTimeout(() => {
            this.hasError = false;
            this.recoveryCode = '';
        }, 600);
    }

    /** (Re)start the validity countdown from the full window. Cancels any prior countdown. */
    private startCountdown() {
        this.stopCountdown();
        this.isExpired = false;
        this.smsSendFailed = false;
        this.secondsRemaining = this._codeValiditySeconds;

        this._countdownSub = timer(1000, 1000)
            .pipe(
                take(this._codeValiditySeconds),
                map((elapsed) => this._codeValiditySeconds - (elapsed + 1)),
                untilDestroyed(this),
            )
            .subscribe((remaining) => {
                this.secondsRemaining = remaining;
                if (remaining <= 0) {
                    this.isExpired = true;
                }
            });
    }

    private stopCountdown() {
        this._countdownSub?.unsubscribe();
        this._countdownSub = null;
        this.secondsRemaining = 0;
        this.isExpired = false;
        this.smsSendFailed = false;
    }
}
